import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  QrCode, 
  Mail, 
  KeyRound, 
  Smartphone, 
  RefreshCw, 
  Trash, 
  CheckCircle, 
  AlertTriangle, 
  Info,
  Star,
  Pencil,
  Save,
  Loader2,
  X
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

/**
 * MFATester Component for testing Multi-Factor Authentication features
 * This component allows developers to test:
 * - TOTP setup and verification
 * - Email MFA setup and verification
 * - Trusted device management
 * - MFA settings management
 */
const MFATester = () => {
  const [loading, setLoading] = useState({});
  const [mfaStatus, setMfaStatus] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [deviceName, setDeviceName] = useState('My Device');
  const [mfaMethods, setMfaMethods] = useState([]);
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [editedName, setEditedName] = useState('');
  
  // On component mount, fetch all relevant MFA data
  useEffect(() => {
    fetchMfaStatus();
    fetchTrustedDevices();
    fetchMfaMethods();
    
    // Log initial state for debugging
    console.log('Initial mfaMethods state:', mfaMethods);
  }, []);
  
  // Debug when mfaMethods changes
  useEffect(() => {
    console.log('mfaMethods updated:', mfaMethods);
  }, [mfaMethods]);

  // Fetch the current MFA status from the server
  const fetchMfaStatus = async () => {
    setLoading(prev => ({ ...prev, status: true }));
    try {
      const response = await api.get('/api/mfa/status/');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
      toast.error('Failed to fetch MFA status');
    } finally {
      setLoading(prev => ({ ...prev, status: false }));
    }
  };

  // Fetch trusted devices
  const fetchTrustedDevices = async () => {
    setLoading(prev => ({ ...prev, devices: true }));
    try {
      const response = await api.get('/api/mfa/trusted-devices/');
      console.log('Trusted devices API response:', response.data);
      
      // Handle paginated response
      if (response.data && response.data.results) {
        setTrustedDevices(response.data.results);
      } else {
        setTrustedDevices(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch trusted devices:', error);
    } finally {
      setLoading(prev => ({ ...prev, devices: false }));
    }
  };
  
  // Fetch MFA methods (devices)
  const fetchMfaMethods = async () => {
    setLoading(prev => ({ ...prev, methods: true }));
    try {
      const response = await api.get('/api/mfa/methods/');
      console.log('MFA methods API response:', response.data);
      
      // Django REST Framework typically returns paginated responses
      // with results in a 'results' array
      if (response.data && response.data.results) {
        console.log('Setting MFA methods from results array:', response.data.results);
        setMfaMethods(response.data.results);
      } else {
        console.log('Setting MFA methods directly:', response.data);
        setMfaMethods(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch MFA methods:', error);
      toast.error('Failed to fetch MFA methods');
    } finally {
      setLoading(prev => ({ ...prev, methods: false }));
    }
  };
  
  // Delete an MFA method
  const deleteMfaMethod = async (methodId) => {
    setLoading(prev => ({ ...prev, [`method_${methodId}`]: true }));
    try {
      await api.delete(`/api/mfa/methods/${methodId}/`);
      toast.success('MFA device deleted successfully');
      fetchMfaMethods(); // Refresh the list after deletion
      fetchMfaStatus(); // Also refresh status as it may have changed
    } catch (error) {
      console.error('Failed to delete MFA method:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to delete MFA method: ${errorMsg}`);
      } else {
        toast.error('Failed to delete MFA method');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`method_${methodId}`]: false }));
    }
  };
  
  // Set an MFA method as primary
  const setPrimaryMethod = async (methodId) => {
    setLoading(prev => ({ ...prev, [`primary_${methodId}`]: true }));
    try {
      await api.post(`/api/mfa/methods/${methodId}/set_primary/`);
      toast.success('Primary MFA device updated successfully');
      fetchMfaMethods(); // Refresh the list
    } catch (error) {
      console.error('Failed to set primary MFA method:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to set primary MFA method: ${errorMsg}`);
      } else {
        toast.error('Failed to set primary MFA method');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`primary_${methodId}`]: false }));
    }
  };
  
  // Start editing an MFA method name
  const startEditingMethod = (method) => {
    setEditingMethodId(method.id);
    setEditedName(method.device_name);
  };
  
  // Cancel editing
  const cancelEditingMethod = () => {
    setEditingMethodId(null);
    setEditedName('');
  };
  
  // Save edited MFA method name
  const saveMethodName = async (methodId) => {
    if (!editedName.trim()) {
      toast.error('Device name cannot be empty');
      return;
    }
    
    setLoading(prev => ({ ...prev, [`save_${methodId}`]: true }));
    try {
      await api.patch(`/api/mfa/methods/${methodId}/`, { device_name: editedName.trim() });
      toast.success('Device name updated successfully');
      fetchMfaMethods(); // Refresh the list
      cancelEditingMethod();
    } catch (error) {
      console.error('Failed to update MFA method name:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to update device name: ${errorMsg}`);
      } else {
        toast.error('Failed to update device name');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`save_${methodId}`]: false }));
    }
  };

  // Setup TOTP MFA
  const setupTOTP = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name for your device');
      return;
    }
    
    setLoading(prev => ({ ...prev, totp: true }));
    try {
      const response = await api.post('/api/mfa/totp/setup/', { device_name: deviceName });
      setQrCodeData(response.data);
      toast.success('TOTP setup initiated - scan the QR code with your authenticator app');
    } catch (error) {
      console.error('Failed to setup TOTP:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to setup TOTP: ${errorMsg}`);
      } else {
        toast.error('Failed to setup TOTP');
      }
    } finally {
      setLoading(prev => ({ ...prev, totp: false }));
    }
  };

  // Verify TOTP code
  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(prev => ({ ...prev, verify: true }));
    try {
      await api.post('/api/mfa/totp/verify/', { 
        code: verificationCode,
        device_name: deviceName
      });
      toast.success('TOTP verification successful!');
      setQrCodeData(null); // Clear QR code after successful verification
      fetchMfaStatus(); // Refresh MFA status
      setVerificationCode(''); // Clear the verification code input
    } catch (error) {
      console.error('Failed to verify TOTP:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to verify TOTP: ${errorMsg}`);
      } else {
        toast.error('Failed to verify TOTP code');
      }
    } finally {
      setLoading(prev => ({ ...prev, verify: false }));
    }
  };

  // Setup Email MFA
  const setupEmailMFA = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }
    
    setLoading(prev => ({ ...prev, email: true }));
    try {
      await api.post('/api/mfa/email/setup/', { device_name: deviceName });
      setEmailSent(true);
      toast.success('Verification email sent');
    } catch (error) {
      console.error('Failed to setup Email MFA:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to setup Email MFA: ${errorMsg}`);
      } else {
        toast.error('Failed to setup Email MFA');
      }
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  // Verify Email MFA
  const verifyEmailMFA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(prev => ({ ...prev, verifyEmail: true }));
    try {
      await api.post('/api/mfa/email/verify/', { 
        code: verificationCode,
        device_name: deviceName 
      });
      toast.success('Email verification successful!');
      setEmailSent(false); // Reset email sent status
      fetchMfaStatus(); // Refresh MFA status
      setVerificationCode(''); // Clear the verification code input
    } catch (error) {
      console.error('Failed to verify email code:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to verify email: ${errorMsg}`);
      } else {
        toast.error('Failed to verify email code');
      }
    } finally {
      setLoading(prev => ({ ...prev, verifyEmail: false }));
    }
  };

  // Toggle MFA settings
  const toggleMfaSettings = async (enabled) => {
    setLoading(prev => ({ ...prev, settings: true }));
    try {
      await api.put('/api/mfa/settings/', {
        is_mfa_enabled: enabled
      });
      toast.success(`MFA ${enabled ? 'enabled' : 'disabled'} successfully`);
      fetchMfaStatus(); // Refresh MFA status
    } catch (error) {
      console.error('Failed to update MFA settings:', error);
      toast.error('Failed to update MFA settings');
    } finally {
      setLoading(prev => ({ ...prev, settings: false }));
    }
  };

  // Remove a trusted device
  const removeTrustedDevice = async (deviceId) => {
    setLoading(prev => ({ ...prev, [`device_${deviceId}`]: true }));
    try {
      await api.delete(`/api/mfa/trusted-devices/${deviceId}/`);
      toast.success('Trusted device removed');
      fetchTrustedDevices(); // Refresh trusted devices
    } catch (error) {
      console.error('Failed to remove trusted device:', error);
      toast.error('Failed to remove trusted device');
    } finally {
      setLoading(prev => ({ ...prev, [`device_${deviceId}`]: false }));
    }
  };

  // Test MFA challenge
  const testMfaChallenge = async () => {
    setLoading(prev => ({ ...prev, challenge: true }));
    try {
      // Get the current user's username
      const userResponse = await api.get('/api/user/info/');
      const username = userResponse.data.username || 'test_user';
      
      const response = await api.post('/api/mfa/login/challenge/', {
        username: username // Use the actual logged-in username
      });
      toast.success('MFA challenge initiated');
      console.log('MFA Challenge response:', response.data);
    } catch (error) {
      console.error('Failed to initiate MFA challenge:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to initiate MFA challenge: ${errorMsg}`);
      } else {
        toast.error('Failed to initiate MFA challenge');
      }
    } finally {
      setLoading(prev => ({ ...prev, challenge: false }));
    }
  };

  return (
    <div className="space-y-8">
      {/* MFA Status */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <Shield className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MFA Status</h3>
          <button 
            onClick={fetchMfaStatus}
            className="ml-auto rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {loading.status ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : mfaStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-900/40">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">MFA Enabled</div>
              <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                {mfaStatus.is_mfa_enabled ? (
                  <span className="flex items-center text-success-600 dark:text-success-400">
                    <CheckCircle className="mr-1 h-4 w-4" /> Enabled
                  </span>
                ) : (
                  <span className="text-danger-600 dark:text-danger-400">Disabled</span>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-900/40">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Available Methods</div>
              <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                {mfaStatus.available_methods?.length 
                  ? mfaStatus.available_methods.join(', ') 
                  : 'None'}
              </div>
            </div>
            {mfaStatus.is_mfa_required !== undefined && (
              <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-900/40">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">MFA Required</div>
                <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {mfaStatus.is_mfa_required ? 'Yes' : 'No'}
                </div>
              </div>
            )}
            {mfaStatus.remember_device_days !== undefined && (
              <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-gray-900/40">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Remember Device For</div>
                <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {mfaStatus.remember_device_days} days
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500 dark:text-gray-400">
            No MFA status information available
          </div>
        )}

        <div className="flex mt-6 space-x-4 justify-end">
          <motion.button
            onClick={() => toggleMfaSettings(true)}
            disabled={loading.settings || (mfaStatus?.is_mfa_enabled)}
            className="btn btn-primary"
          >
            Enable MFA
          </motion.button>
          <motion.button
            onClick={() => toggleMfaSettings(false)}
            disabled={loading.settings || !(mfaStatus?.is_mfa_enabled)}
            className="btn btn-danger"
          >
            Disable MFA
          </motion.button>
        </div>
      </motion.div>

      {/* TOTP Setup */}
      {/* Device Name Input - common for all MFA methods */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Device Settings</h3>
        </div>

        <div className="mb-4 rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
          <div className="flex">
            <Info className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-medium">How to use this tester:</p>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Enter a device name below (e.g., "iPhone 13", "Work Laptop")</li>
                <li>Choose an MFA method to set up (TOTP or Email)</li>
                <li>Follow the setup instructions for your chosen method</li>
                <li>Verify using the code from your authenticator app or email</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="device-name" className="label">
            Device Name
          </label>
          <div className="flex">
            <input
              id="device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Enter a recognizable name for this device"
              className="input flex-1"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This should identify the physical device you're using for MFA (e.g., "Jake's iPhone")
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <QrCode className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">TOTP Authentication</h3>
        </div>

        <p className="text-gray-600 mb-4 dark:text-gray-400">
          Test Time-Based One-Time Password (TOTP) authentication setup and verification.
        </p>

        {qrCodeData && (
          <div className="mb-6">
            <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-lg">
                {/* Display QR code image */}
                <img 
                  src={qrCodeData.qr_code} 
                  alt="TOTP QR Code" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <div className="text-sm text-gray-600 mb-2 dark:text-gray-400">
                Scan this QR code with your authenticator app
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-1 dark:text-gray-400">Or use the secret key:</span>
                <code className="select-all rounded-md bg-gray-100 px-2 py-1 font-mono text-sm dark:bg-gray-700">
                  {qrCodeData.secret}
                </code>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-4">
            <motion.button
              onClick={setupTOTP}
              disabled={loading.totp}
              className="btn btn-primary"
            >
              {loading.totp ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <QrCode className="h-4 w-4" />
                  Generate QR Code
                </>
              )}
            </motion.button>
          </div>

          <div className="mt-4">
            <label htmlFor="verification-code" className="label">
              Verification Code
            </label>
            <div className="flex">
              <input
                id="verification-code"
                type="text"
                maxLength="6"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                className="input flex-1 rounded-r-none"
              />
              <motion.button
                onClick={verifyTOTP}
                disabled={loading.verify || !verificationCode}
                className="btn btn-primary rounded-l-none"
              >
                {loading.verify ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Verify'
                )}
              </motion.button>
            </div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>
        </div>
      </motion.div>

      {/* Email MFA Setup */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <Mail className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Authentication</h3>
        </div>

        <div>
          {!emailSent ? (
            <div>
              <div className="mb-4">
                <p className="text-gray-700 mb-2 dark:text-gray-300">
                  Email MFA sends a verification code to your email address for an extra layer of security.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-medium text-gray-700 mb-2 dark:text-gray-300">How it works:</h4>
                  <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1 dark:text-gray-400">
                    <li>Click "Send Verification Email" (make sure you've entered a device name first)</li>
                    <li>Check your email for the verification code</li>
                    <li>Enter the 6-digit code from the email to complete setup</li>
                  </ol>
                </div>
              </div>
              <motion.button
                onClick={setupEmailMFA}
                disabled={loading.email}
                className="btn btn-primary"
              >
                {loading.email ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Send Verification Email
                  </>
                )}
              </motion.button>
            </div>
          ) : (
            <div>
              <div className="mb-4 rounded-lg border border-success-500/25 bg-success-50 p-4 dark:bg-success-500/10">
                <div className="flex">
                  <CheckCircle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                  <p className="text-sm text-success-700 dark:text-success-400">Verification email sent! Check your inbox for a 6-digit code.</p>
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="email-code" className="label">
                  Enter Code From Your Email
                </label>
                <div className="flex">
                  <input
                    id="email-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    className="input"
                  />
                  <motion.button
                    onClick={verifyEmailMFA}
                    disabled={loading.verifyEmail || verificationCode.length !== 6}
                    className="btn btn-primary ml-4"
                  >
                    {loading.verifyEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Verify
                      </>
                    )}
                  </motion.button>
                </div>
                <div className="mt-4">
                  <motion.button
                    onClick={() => {
                      setEmailSent(false);
                      setVerificationCode('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Trusted Devices */}
      {/* MFA Methods/Devices Management */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MFA Devices</h3>
          <button 
            onClick={fetchMfaMethods}
            className="ml-auto rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <p className="text-gray-600 mb-4 dark:text-gray-400">
          Manage your MFA devices and methods. You can rename, delete, or set a device as primary.
        </p>

        {/* Debugging info */}
        <div className="text-sm text-gray-500 mb-4 dark:text-gray-400">
          Debug - Methods state: {mfaMethods ? `${mfaMethods.length} methods` : 'null'}
          {mfaMethods && mfaMethods.length > 0 && (
            <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-gray-100 p-2 font-mono text-xs dark:bg-gray-700">
              {JSON.stringify(mfaMethods, null, 2)}
            </pre>
          )}
        </div>
        
        {loading.methods ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : mfaMethods && mfaMethods.length > 0 ? (
          <div className="space-y-4">
            {mfaMethods.map((method) => (
              <div key={method.id} className={`overflow-hidden rounded-lg border ${method.is_primary ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Device info */}
                    <div className="flex items-center">
                      {method.method_type === 'totp' ? (
                        <QrCode className={`mr-3 h-5 w-5 ${method.is_primary ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      ) : (
                        <Mail className={`mr-3 h-5 w-5 ${method.is_primary ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      )}
                      
                      {/* If currently editing this method */}
                      {editingMethodId === method.id ? (
                        <div className="flex items-center">
                          <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            className="input mr-2 h-8 w-auto px-2"
                            autoFocus
                          />
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => saveMethodName(method.id)}
                              disabled={loading[`save_${method.id}`]}
                              className="rounded-md p-1 text-success-600 transition-colors hover:bg-success-50 hover:text-success-700 dark:text-success-400 dark:hover:bg-success-500/10"
                            >
                              {loading[`save_${method.id}`] ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                            </button>
                            <button 
                              onClick={cancelEditingMethod}
                              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {method.device_name}
                            </span>
                            {method.is_primary && (
                              <span className="badge badge-brand ml-2">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Type: {method.method_type === 'totp' ? 'Authenticator App' : 'Email'}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    {editingMethodId !== method.id && (
                      <div className="flex space-x-3">
                        {/* Edit button */}
                        <button
                          onClick={() => startEditingMethod(method)}
                          className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                          title="Rename device"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        
                        {/* Set as primary button (disabled if already primary) */}
                        {!method.is_primary && (
                          <button
                            onClick={() => setPrimaryMethod(method.id)}
                            disabled={loading[`primary_${method.id}`]}
                            className="rounded-md p-1 text-warning-500 transition-colors hover:bg-warning-50 hover:text-warning-700 dark:hover:bg-warning-500/10"
                            title="Set as primary device"
                          >
                            {loading[`primary_${method.id}`] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Star className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        
                        {/* Delete button */}
                        <button
                          onClick={() => deleteMfaMethod(method.id)}
                          disabled={loading[`method_${method.id}`]}
                          className="rounded-md p-1 text-danger-500 transition-colors hover:bg-danger-50 hover:text-danger-700 dark:hover:bg-danger-500/10"
                          title="Delete device"
                        >
                          {loading[`method_${method.id}`] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No MFA devices found. Set up a new device using the options below.
          </div>
        )}
      </motion.div>
      
      {/* Trusted Devices */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Trusted Devices</h3>
          <button 
            onClick={fetchTrustedDevices}
            className="ml-auto rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <p className="text-gray-600 mb-4 dark:text-gray-400">
          Manage devices that are trusted for MFA.
        </p>

        {loading.devices ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : trustedDevices && trustedDevices.length > 0 ? (
          <div className="space-y-3">
            {trustedDevices.map((device) => (
              <div key={device.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <div className="flex items-center">
                  <Smartphone className="mr-3 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{device.device_name || 'Unknown Device'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Trusted until: {new Date(device.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeTrustedDevice(device.id)}
                  disabled={loading[`device_${device.id}`]}
                  className="rounded-md p-1 text-danger-500 transition-colors hover:bg-danger-50 hover:text-danger-700 dark:hover:bg-danger-500/10 ml-4"
                >
                  {loading[`device_${device.id}`] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No trusted devices found
          </div>
        )}
      </motion.div>

      {/* MFA Challenge Testing */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }}
        className="card p-6"
      >
        <div className="flex items-center mb-4">
          <KeyRound className="mr-2.5 h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Login Flow MFA Challenge Testing</h3>
        </div>

        <div className="mb-4">
          <p className="text-gray-700 mb-2 dark:text-gray-300">
            This tests what happens during a login attempt when MFA is enabled for a user.
          </p>
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-700 mb-2 dark:text-gray-300">What this does:</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1 dark:text-gray-400">
              <li>Simulates a user attempting to log in with correct username/password</li>
              <li>Triggers the MFA challenge step that would occur during login</li>
              <li>Returns information about which MFA methods are available</li>
            </ol>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-500/10">
          <div className="flex">
            <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
            <div className="text-sm text-warning-800 dark:text-warning-400">
              <p className="font-medium">Before testing:</p>
              <ul className="list-disc ml-5 mt-1">
                <li>You must have at least one MFA method set up and enabled</li>
                <li>This will use your current logged-in user for the test</li>
              </ul>
            </div>
          </div>
        </div>

        <motion.button
          onClick={testMfaChallenge}
          disabled={loading.challenge}
          className="btn btn-primary"
        >
          {loading.challenge ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <KeyRound className="h-4 w-4" />
              Test MFA Challenge
            </>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default MFATester;
